import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { subscriptionService } from '../../../services/subscriptionService';

const PlanComparison = ({ plans, currentSubscription, onUpgrade }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (planId) => {
    setLoading(planId);
    try {
      await onUpgrade(planId, billingCycle);
    } finally {
      setLoading(null);
    }
  };

  const isCurrentPlan = (planId) => {
    return currentSubscription?.planId === planId;
  };

  const calculateSavings = (monthly, yearly) => {
    if (!yearly || !monthly) return 0;
    const yearlyMonthly = yearly / 12;
    const savings = ((monthly - yearlyMonthly) / monthly) * 100;
    return Math.round(savings);
  };

  return (
    <div className="space-y-6">
      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-medium ${
          billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'
        }`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          className="relative w-14 h-7 bg-muted rounded-full transition-colors hover:bg-muted/80"
        >
          <div className={`absolute top-1 left-1 w-5 h-5 bg-primary rounded-full transition-transform ${
            billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-0'
          }`} />
        </button>
        <span className={`text-sm font-medium ${
          billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'
        }`}>
          Yearly
          {plans?.[0]?.priceYearly && (
            <span className="ml-2 text-xs text-green-600 font-semibold">
              Save {calculateSavings(plans?.[0]?.priceMonthly, plans?.[0]?.priceYearly)}%
            </span>
          )}
        </span>
      </div>
      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans?.map((plan) => {
          const price = billingCycle === 'yearly' ? plan?.priceYearly : plan?.priceMonthly;
          const isCurrent = isCurrentPlan(plan?.id);
          const isPopular = plan?.name === 'Professional';

          return (
            <div
              key={plan?.id}
              className={`bg-card border rounded-lg overflow-hidden transition-all hover:shadow-lg ${
                isPopular ? 'border-primary shadow-md scale-105' : 'border-border'
              }`}
            >
              {/* Header */}
              <div className={`px-6 py-4 ${
                isPopular ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold">{plan?.name}</h3>
                  {isPopular && (
                    <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">
                      Popular
                    </span>
                  )}
                  {isCurrent && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                      Current
                    </span>
                  )}
                </div>
                <p className={`text-sm ${
                  isPopular ? 'text-primary-foreground/80' : 'text-muted-foreground'
                }`}>
                  {plan?.description}
                </p>
              </div>

              {/* Pricing */}
              <div className="px-6 py-6 border-b border-border">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold">
                    {subscriptionService?.formatAmount(price, plan?.currency)}
                  </span>
                  <span className="text-muted-foreground">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                </div>
                {billingCycle === 'yearly' && plan?.priceYearly && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {subscriptionService?.formatAmount(plan?.priceYearly / 12, plan?.currency)}/month billed annually
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="px-6 py-6 space-y-3">
                {plan?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Icon name="Check" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Limits */}
              <div className="px-6 py-4 bg-muted/50 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">User Seats</span>
                  <span className="font-medium">
                    {plan?.seatLimit === -1 ? 'Unlimited' : plan?.seatLimit}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Sessions/Month</span>
                  <span className="font-medium">
                    {plan?.sessionLimit === -1 ? 'Unlimited' : plan?.sessionLimit}
                  </span>
                </div>
              </div>

              {/* Action Button */}
              <div className="px-6 py-6">
                {isCurrent ? (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    disabled
                  >
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleUpgrade(plan?.id)}
                    className="w-full"
                    iconName={loading === plan?.id ? 'Loader2' : 'ArrowRight'}
                    iconPosition="right"
                    iconClassName={loading === plan?.id ? 'animate-spin' : ''}
                    disabled={loading !== null}
                  >
                    {currentSubscription ? 'Upgrade to ' : 'Choose '}{plan?.name}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Additional Info */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div className="space-y-2">
            <p className="font-medium">Plan Information</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• All plans include secure data encryption and HIPAA compliance</li>
              <li>• Upgrade or downgrade anytime with prorated billing</li>
              <li>• Cancel anytime - no long-term contracts required</li>
              <li>• 24/7 technical support available for all plans</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanComparison;